import React, {useMemo, useState} from 'react';
import {Grid, IconButton, makeStyles} from '@material-ui/core';
import {useFormik} from 'formik';
import {useTranslation} from 'react-i18next';
import IconWithTooltip from '../Tooltip/IconWithTooltip';
import TextField from '../TextField/TextField';
import DialogContainer, {DialogButton} from '../AlertDialog/DialogContainer';
import {Colors} from '../../styles/Colors';
import * as Yup from 'yup';
import classNames from 'classnames';
import i18n from '../../services/i18n';
import {useDispatch, useSelector} from 'react-redux';
import {actions} from '../../store';
import Loader from '../Loader/Loader';
import CustomizedTooltip from '../Tooltip/Tooltip';
import {APIErrorInterface, ReduxState} from '../../store/types';
import {getConfigFromPasswordRulesOrDefault} from '../../utils/passwordGenerator';
import CopyContentIcon from '../CopyContentIcon/CopyContentIcon';
import PasswordTextField from '../PasswordTextField/PasswordTextField';

const SipValidationSchema = Yup.object().shape({
    servicePassword: Yup.string().required(
        i18n.t<string>('errors:common.emptyInput'),
    ),
});

export interface SipDialogFormProps {
    registrationName: string;
    servicePassword: string;
    userAgent: string;
    sipContact: string;
    iAccount: number;
}

export interface SipDialogProps extends SipDialogFormProps {
    onSave?: () => void;
    isOpen: boolean;
    toggleVisibility?: (refresh: boolean) => void;
    savingSipInfo?: boolean;
    apiErrors?: APIErrorInterface;
}

const useStyles = makeStyles(() => ({
    itemsContainer: {
        backgroundColor: Colors.LightGray,
        padding: '16px 24px',
        display: 'flex',
        flexDirection: 'column',

        '& .MuiFormControl-root': {
            flex: 1,
            height: 'auto',
            marginTop: 25,
        },

        '& > :first-child': {
            marginTop: 0,
        },

        '& .MuiInputBase-root': {
            height: 60,
            marginTop: 0,
        },

        '& .MuiInputLabel-shrink': {
            transform: 'translate(0, 10px) scale(0.75)',
        },
        '& .MuiInputLabel-root': {
            marginLeft: 10,
        },
    },
    inputs: {
        '& .MuiFormControl-root:first-of-type': {
            marginRight: 90,
        },

        '& .MuiInputBase-root': {
            height: 60,
            marginTop: 0,
        },
    },
    visibilityIcon: {
        marginRight: 6,
    },
    primaryActionButton: {
        '& .MuiButton-label': {
            color: Colors.Primary,
        },
    },
    header: {
        '& .MuiTypography-h6': {
            fontWeight: 'bold',
            fontSize: 18,
        },
    },
    modalContainer: {
        '& .MuiDialogContent-root': {
            width: 640,
            backgroundColor: Colors.LightGray,
        },
    },
    iconColors: {
        '& .MuiSvgIcon-root': {
            '& path': {
                fill: `${Colors.PlaceHolder} !important`,
                fillOpacity: 1,
            },
        },
    },
    wait: {
        position: 'absolute',
        display: 'flex',
        top: '50%',
        left: '50%',
        zIndex: 100,
    },
    form: {
        padding: 0,
        margin: 0
    },
    tooltip: {
        overflowWrap: 'anywhere',
    },
}));

const SipDialog: React.VFC<SipDialogProps> = ({
    registrationName,
    servicePassword,
    sipContact,
    iAccount,
    userAgent,
    isOpen,
    toggleVisibility,
    savingSipInfo,
    apiErrors,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [copyRegNameVisible, setCopyRegNameVisible] = useState(false);
    const [copyPasswordNameVisible, setCopyPasswordNameVisible] = useState(false);

    const configData = useSelector((state: ReduxState) => state.generic.configData);

    const initialValues = useMemo(
        () => ({
            registrationName,
            servicePassword,
            userAgent: userAgent || t('common:notSet'),
            sipContact,
            iAccount,
        }),
        [registrationName, servicePassword, userAgent, sipContact],
    );

    const {
        values,
        handleSubmit,
        handleChange,
        errors,
        resetForm,
        setFieldValue,
        setFieldError,
    } = useFormik<SipDialogFormProps>({
        initialValues,
        onSubmit: () => {
            dispatch(
                actions.postServicePassword.request({
                    i_account: iAccount,
                    h323_password: values.servicePassword,
                    callback: () => {
                        toggleVisibility?.(true);
                    },
                }),
            );
        },
        enableReinitialize: true,
        validationSchema: SipValidationSchema,
        validateOnChange: false,
        validateOnBlur: false,
    });

    const handleChangeVisible = () => {
        setCopyRegNameVisible((prevState) => !prevState);
    };

    const handlePasswordCopyVisible = () => {
        setCopyPasswordNameVisible((prevState) => !prevState);
    };

    const setNewPassword = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setFieldValue('servicePassword', e.target.value.replaceAll(' ', ''))
    };

    return (
        <DialogContainer
            isOpen={isOpen}
            dataQa="sip-dialog"
            header={t('screens:extensions.sipInfo')}
            headerClass={classes.header}
            className={classes.modalContainer}
            dialogActionsButtons={[
                <DialogButton
                    className={classes.primaryActionButton}
                    key="cancel"
                    label={t('common:cancel')}
                    onClick={() => {
                        toggleVisibility?.(false);
                        setTimeout(() => {
                            resetForm();
                        }, 200);
                    }}
                />,
                <DialogButton
                    className={classNames(
                        (values.registrationName !== registrationName ||
                            (userAgent && values.userAgent !== userAgent) ||
                            values.servicePassword !== servicePassword) &&
                            classes.primaryActionButton,
                    )}
                    key="save"
                    label={t('common:save')}
                    onClick={handleSubmit}
                    disabled={
                        !(
                            values.registrationName !== registrationName ||
                            (userAgent && values.userAgent !== userAgent) ||
                            values.servicePassword !== servicePassword
                        )
                    }
                />,
            ]}
        >
            {savingSipInfo && (
                <div className={classes.wait}>
                    <Loader dataQa={'sip-ingo-loader'} />
                </div>
            )}
            <form
                onSubmit={handleSubmit}
                autoComplete="off"
                data-testid="sipdialog-form"
                className={classes.form}
            >
                <Grid item className={classes.itemsContainer}>
                    <TextField
                        onMouseEnter={handleChangeVisible}
                        onMouseLeave={handleChangeVisible}
                        id="registrationName"
                        label={t('screens:extensions.registrationName')}
                        onChange={handleChange}
                        value={values.registrationName}
                        icon={
                            <>
                                <div
                                    style={{
                                        visibility: copyRegNameVisible
                                            ? 'visible'
                                            : 'hidden',
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        className={classes.visibilityIcon}
                                        data-testid="registration-name-visibility-button"
                                        data-qa="registration-name-visibility-button"
                                    >
                                        <CopyContentIcon
                                            copyContent={
                                                values.registrationName
                                            }
                                            tooltipMsg={t(
                                                'tooltips:extensions.copyRegistrationName',
                                            )}
                                        />
                                    </IconButton>
                                </div>
                                <IconWithTooltip
                                    dataQa="sip-registration-name-tooltip"
                                    tooltipText={t(
                                        'tooltips:extensions.registrationName',
                                    )}
                                />
                            </>
                        }
                        iconPosition="end"
                        dataQa="sip-registration-name-input"
                        readOnly
                        noTooltip
                        helperText={
                            apiErrors
                                ? t('errors:common.emptyInput')
                                : errors.servicePassword
                        }
                    />

                    <TextField
                        id="userAgent"
                        label={t('screens:extensions.userAgent')}
                        onChange={handleChange}
                        value={values.userAgent}
                        dataQa="sip-user-agent-input"
                        readOnly
                    />

                    <PasswordTextField
                        onMouseEnter={handlePasswordCopyVisible}
                        onMouseLeave={handlePasswordCopyVisible}
                        id="servicePassword"
                        label={t('screens:extensions.servicePassword')}
                        value={values.servicePassword}
                        setFieldValue={setFieldValue}
                        onChange={setNewPassword}
                        dataQa="sip-service-password-input"
                        setFieldError={setFieldError}
                        error={
                            errors.servicePassword
                        }
                        isRequired={true}
                        passwordRulesConfig={getConfigFromPasswordRulesOrDefault(
                            configData
                        )}
                        maxLength={32}
                        icon={
                            <>
                                <div
                                    style={{
                                        visibility: copyPasswordNameVisible
                                            ? 'visible'
                                            : 'hidden',
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        className={classes.visibilityIcon}
                                        data-testid="service-password-visibility-button"
                                        data-qa="service-password-visibility-button"
                                    >
                                        <CopyContentIcon
                                            copyContent={values.servicePassword}
                                            tooltipMsg={t(
                                                'tooltips:extensions.copyServicePassword',
                                            )}
                                        />
                                    </IconButton>
                                </div>
                            </>
                        }
                    />
                    
                    <CustomizedTooltip
                        title={sipContact}
                        interactive
                        copy
                        classes={{text: classes.tooltip}}
                    >
                        <TextField
                            id="contactDetails"
                            label={t('screens:extensions.contactDetails')}
                            onChange={handleChange}
                            value={sipContact}
                            dataQa="sip-user-contact-details"
                            readOnly
                            noTooltip
                        />
                    </CustomizedTooltip>

                </Grid>
            </form>
        </DialogContainer>
    );
};

export default SipDialog;
